import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { useLanguage } from "../../locales/LanguageContext";

const FirstFrame = ({
  firstUnit: propFirstUnit,
  secondUnit: propSecondUnit,
  exitFrame: propExitFrame,
}) => {
  const { fps } = useVideoConfig();
  const frame = useCurrentFrame();
  const { t, lang, firstUnit: ctxFirstUnit, secondUnit: ctxSecondUnit } =
    useLanguage();

  const firstUnit = propFirstUnit || ctxFirstUnit;
  const secondUnit = propSecondUnit || ctxSecondUnit;

  const currencyPairText =
    firstUnit && secondUnit
      ? lang === "en"
        ? `${firstUnit}/${secondUnit}`
        : lang === "tc-CN" || lang === "yue-CN"
          ? `${firstUnit}兌${secondUnit}`
          : `${firstUnit}兑${secondUnit}`
      : t?.firstFrame?.line4 || "EUR/GBP";

  const segmentsOf = (text) =>
    lang === "en" ? text.split(" ") : Array.from(text);
  const segmentsOfUnit = (text) =>
    lang === "en" ? text.split("") : Array.from(text);

  const titleSegmentsOf = (text) => {
    const lines = text.split("\n");
    const segments = [];
    lines.forEach((line, lineIndex) => {
      if (lineIndex > 0) segments.push({ type: "break" });
      segmentsOf(line).forEach((word) =>
        segments.push({ type: "word", text: word }),
      );
    });
    return segments;
  };

  const exitFrame = propExitFrame !== undefined ? propExitFrame : 550;
  const startDelay = exitFrame - fps;
  const progress = spring({
    fps,
    frame: frame - startDelay,
    config: { damping: 10, mass: 1, stiffness: 50 },
    reverse: true,
  });
  const scale = interpolate(progress, [0, 1], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const exitOpacity = interpolate(progress, [0, 1], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const SHOW_OTHERS = 120;

  const moveUpProgress = spring({
    fps,
    frame: frame - SHOW_OTHERS,
    config: { damping: 14 },
  });

  const translateXLine1 = interpolate(moveUpProgress, [0, 1], [200, 0]);
  const translateY = interpolate(moveUpProgress, [0, 1], [130, 0]);

  const othersOpacity = interpolate(
    frame,
    [SHOW_OTHERS, SHOW_OTHERS + 15],
    [0, 1],
    {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    },
  );

  const calendarPopProgress = Math.max(
    0,
    spring({
      fps,
      frame: frame - SHOW_OTHERS,
      config: { damping: 12, stiffness: 80 },
    }),
  );

  return (
    <AbsoluteFill
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        width: "100%",
        color: "white",
        fontFamily: "'Noto Sans SC', sans-serif",
        transform: `scale(${scale})`,
        opacity: exitOpacity,
      }}
    >
      <div
        style={{
          position: "absolute",
          display: "flex",
          padding: "50px",
          flexDirection: "column",
          alignItems: "center",
          transform: `translateY(${translateY}px)`,
        }}
      >
        <p
          style={{
            fontSize: "4.5rem",
            fontWeight: "normal",
            whiteSpace: "pre",
            marginBottom: "20px",
            color: "#42c22f",
            transform: `translateX(${translateXLine1}px)`,
          }}
        >
          {segmentsOf(t.firstFrame.line1).map((word, wordIndex, arr) => {
            const wordDelay = wordIndex * 10;

            const progress = interpolate(
              frame - SHOW_OTHERS - wordDelay,
              [0, 30],
              [0, 1],
              {
                extrapolateLeft: "clamp",
                extrapolateRight: "clamp",
              },
            );

            const scale = interpolate(progress, [0, 1], [1.3, 1.0]);
            const translateY = interpolate(progress, [0, 1], [-30, 0]);
            const opacity = interpolate(progress, [0, 1], [0, 1]);

            return (
              <span
                key={wordIndex}
                style={{
                  display: "inline-block",
                  transform: `translateY(${translateY}px) scale(${scale})`,
                  opacity: opacity,
                  marginRight:
                    lang === "en" && wordIndex < arr.length - 1
                      ? "0.5rem"
                      : "0",
                }}
              >
                {word}
              </span>
            );
          })}
        </p>
        <h1
          style={{
            fontSize: "7rem",
            lineHeight: "0.8",
            fontWeight: "bold",
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: lang === "en" ? "1.5rem" : "0.5rem",
            margin: 0,
            marginBottom: "60px",
            padding: "0",
            textAlign: "center",
          }}
        >
          {(() => {
            let wordCounter = 0;
            return titleSegmentsOf(t.firstFrame.title).map(
              (segment, segmentIndex) => {
                if (segment.type === "break") {
                  return (
                    <span
                      key={segmentIndex}
                      style={{ flexBasis: "100%", height: 0 }}
                    />
                  );
                }

                const wordIndex = wordCounter++;
                const wordDelay = wordIndex * 10;

                const wordProgress = spring({
                  fps,
                  frame: frame - wordDelay,
                  config: { damping: 15, stiffness: 120 },
                  delay: 10,
                });

                const scale = interpolate(wordProgress, [0, 1], [5, 1.0]);
                const opacity = wordProgress;

                return (
                  <span
                    key={segmentIndex}
                    style={{
                      display: "inline-block",
                      transform: `scale(${scale})`,
                      opacity: opacity,
                    }}
                  >
                    {segment.text}
                  </span>
                );
              },
            );
          })()}
        </h1>
        {t.firstFrame.line3 && (
          <p
            style={{
              fontSize: "3rem",
              fontWeight: "normal",
              whiteSpace: "pre",
              marginBottom: "20px",
              color: "#42c22f",
            }}
          >
            {segmentsOf(t.firstFrame.line3 ?? "").map(
              (word, wordIndex, arr) => {
                const wordDelay = wordIndex * 10;

                const progress = interpolate(
                  frame - SHOW_OTHERS - wordDelay,
                  [0, 30],
                  [0, 1],
                  {
                    extrapolateLeft: "clamp",
                    extrapolateRight: "clamp",
                  },
                );

                const scale = interpolate(progress, [0, 1], [1.3, 1.0]);
                const translateY = interpolate(progress, [0, 1], [-30, 0]);
                const opacity = interpolate(progress, [0, 1], [0, 1]);

                return (
                  <span
                    key={wordIndex}
                    style={{
                      display: "inline-block",
                      transform: `translateY(${translateY}px) scale(${scale})`,
                      opacity: opacity,
                      marginRight:
                        lang === "en" && wordIndex < arr.length - 1
                          ? "0.5rem"
                          : "0",
                    }}
                  >
                    {word}
                  </span>
                );
              },
            )}
          </p>
        )}
        <svg
          width="360"
          height="240"
          viewBox="0 0 200 160"
          style={{
            marginTop: "10px",
            marginBottom: "10px",
            opacity: othersOpacity,
            overflow: "visible",
          }}
        >
          <defs>
            <clipPath id="calendarCardClip">
              <rect x="0" y="0" width="200" height="160" rx="28" />
            </clipPath>
            <filter
              id="calendarCardShadow"
              x="-20%"
              y="-20%"
              width="140%"
              height="140%"
            >
              <feDropShadow
                dx="0"
                dy="4"
                stdDeviation="5"
                floodColor="#000000"
                floodOpacity="0.2"
              />
            </filter>
          </defs>

          <rect
            x="0"
            y="0"
            width="200"
            height="160"
            rx="28"
            fill="#FFFFFF"
            filter="url(#calendarCardShadow)"
          />

          <g clipPath="url(#calendarCardClip)">
            <g
              transform={`translate(0 64) scale(1 ${calendarPopProgress}) translate(0 -64)`}
            >
              <rect x="0" y="0" width="200" height="64" fill="#1877F2" />
              <text
                x="100"
                y="52"
                textAnchor="middle"
                fontSize="22"
                fontWeight="bold"
                fill="#FFFFFF"
              >
                {t?.firstFrame?.monthUnit || (lang === "en" ? "April" : "四月")}
              </text>
            </g>

            <g
              transform={`translate(0 64) scale(1 ${calendarPopProgress}) translate(0 -64)`}
            >
              <rect x="0" y="64" width="200" height="96" fill="#FFFFFF" />
              <text
                x="100"
                y="112"
                textAnchor="middle"
                dominantBaseline="central"
                fontSize="84"
                fontWeight="900"
                fontFamily="sans-serif"
                fill="#333333"
              >
                {t?.firstFrame?.day ?? 22}
              </text>
            </g>
          </g>

          {[64, 136].map((cx) => (
            <g
              key={cx}
              transform={`translate(${cx} 30) scale(${calendarPopProgress}) translate(${-cx} -30)`}
            >
              <circle cx={cx} cy="18" r="11" fill="#FFFFFF" />
              <rect
                x={cx - 6}
                y="2"
                width="12"
                height="30"
                rx="6"
                fill="#333333"
              />
            </g>
          ))}
        </svg>
        <div
          style={{
            fontWeight: "600",
            height: "130px",
            display: "flex",
            marginTop: "20px",
            padding: "0",
            gap: lang === "en" ? "1.5rem" : "0.5rem",
            opacity: othersOpacity,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {frame < 300 ? (
            <div>
              {segmentsOfUnit(currencyPairText).map(
                (word, wordIndex, arr) => {
                  const wordDelay = wordIndex * 6;

                  const entranceProgress = spring({
                    fps,
                    frame: frame - SHOW_OTHERS - wordDelay,
                    config: { damping: 12, stiffness: 100 },
                  });

                  const exitProgress = spring({
                    fps,
                    frame: frame - 240 - wordDelay,
                    config: { damping: 12, stiffness: 100 },
                  });

                  const wordProgress = entranceProgress - exitProgress;
                  const scale = interpolate(wordProgress, [0, 1], [1.4, 1.0]);

                  return (
                    <span
                      key={wordIndex}
                      style={{
                        display: "inline-block",
                        fontSize: "7rem",
                        transform: `scale(${scale})`,
                        opacity: Math.max(0, wordProgress),
                        marginRight:
                          lang === "en" && wordIndex < arr.length - 1
                            ? "0.5rem"
                            : "0",
                      }}
                    >
                      {word}
                    </span>
                  );
                },
              )}
            </div>
          ) : (
            segmentsOf(t.firstFrame.line4Replace).map(
              (word, wordIndex, arr) => {
                const wordDelay = wordIndex * 8;

                const wordProgress = spring({
                  fps,
                  frame: frame - 300 - wordDelay,
                  config: { damping: 10, stiffness: 100 },
                });

                const scale = interpolate(wordProgress, [0, 1], [2, 1.0]);

                return (
                  <span
                    key={`replacing-${wordIndex}`}
                    style={{
                      display: "inline-block",
                      fontSize: "5.5rem",
                      transform: `scale(${scale})`,
                      opacity: wordProgress,
                      marginRight:
                        lang === "en" && wordIndex < arr.length - 1
                          ? "0.5rem"
                          : "0",
                    }}
                  >
                    {word}
                  </span>
                );
              },
            )
          )}
        </div>
      </div>
    </AbsoluteFill>
  );
};

export default FirstFrame;
